import React, { useMemo, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import InfoIcon from '../common/InfoIcon'; // Import the InfoIcon

// Helper function to calculate date differences accurately
const calculateDays = (eventDateStr, dueDateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize today's date to midnight

    const eventDate = new Date(eventDateStr);
    eventDate.setUTCHours(0, 0, 0, 0); // Normalize event date

    const dueDate = new Date(dueDateStr);
    dueDate.setUTCHours(0, 0, 0, 0); // Normalize due date

    const remainingDiffTime = dueDate - today;
    const daysRemaining = Math.ceil(remainingDiffTime / (1000 * 60 * 60 * 24));

    const completedDiffTime = today - eventDate;
    const daysCompleted = Math.floor(completedDiffTime / (1000 * 60 * 60 * 24));

    return { 
        daysRemaining: daysRemaining < 0 ? 0 : daysRemaining, 
        daysCompleted: daysCompleted < 0 ? 0 : daysCompleted 
    };
};

// --- Pie Chart Label Rendering ---
const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({ cx, cy, midAngle, outerRadius, percent, name }) => {
    const radius = outerRadius + 25;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const sin = Math.sin(-midAngle * RADIAN);
    const cos = Math.cos(-midAngle * RADIAN);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 20) * cos;
    const my = cy + (outerRadius + 20) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 12;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';

    return (
        <g>
            <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke="#888" fill="none" />
            <circle cx={ex} cy={ey} r={2} fill="#888" stroke="none" />
            <text x={ex + (cos >= 0 ? 1 : -1) * 6} y={ey} textAnchor={textAnchor} fill="#333">{`${name} (${(percent * 100).toFixed(0)}%)`}</text>
        </g>
    );
};


const BreedingDashboard = ({ breedingEvents, onAddEventClick, onUpdateStatusClick }) => {
    
    // --- NEW: State for filters ---
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('All');
    const [filterStatus, setFilterStatus] = useState('All');

    // --- NEW: Memoized filtering logic ---
    const filteredBreedingEvents = useMemo(() => {
        return breedingEvents.filter(event => {
            const searchMatch = searchTerm === '' || event.animalName.toLowerCase().includes(searchTerm.toLowerCase());
            const typeMatch = filterType === 'All' || event.eventType === filterType;
            const statusMatch = filterStatus === 'All' || event.status === filterStatus;
            return searchMatch && typeMatch && statusMatch;
        });
    }, [breedingEvents, searchTerm, filterType, filterStatus]);

    // --- Data processing for the Status Pie Chart ---
    const statusChartData = useMemo(() => {
        const counts = filteredBreedingEvents.reduce((acc, event) => {
            const status = event.status || "Unknown";
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {});
        return Object.entries(counts).map(([name, value]) => ({ name, value }));
    }, [filteredBreedingEvents]);

    // --- Data processing for the Event Type Pie Chart ---
    const eventTypeChartData = useMemo(() => {
        const counts = filteredBreedingEvents.reduce((acc, event) => {
            const type = event.eventType || "Unknown";
            acc[type] = (acc[type] || 0) + 1;
            return acc;
        }, {});
        return Object.entries(counts).map(([name, value]) => ({ name, value }));
    }, [filteredBreedingEvents]);

    const STATUS_PIE_COLORS = {
        'In-Progress': '#f59e0b',
        'Confirmed': '#22c55e',
        'Failed': '#ef4444',
        'Completed (Calved)': '#8b5cf6',
        'Due': '#3b82f6'
    };
    
    const EVENT_TYPE_PIE_COLORS = {
        'Natural Mating': '#0088FE',
        'Artificial Insemination (AI)': '#00C49F',
        'Embryo Transfer': '#FFBB28',
    };

    // --- NEW: Options for select dropdowns ---
    const statusOptions = ["All", "In-Progress", "Confirmed", "Failed", "Completed (Calved)", "Due"];
    const typeOptions = ["All", "Natural Mating", "Artificial Insemination (AI)", "Embryo Transfer"];

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Breeding Management</h2>
                <button 
                    onClick={onAddEventClick}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                >
                    + Record Breeding Event
                </button>
            </div>

            {/* --- NEW: Filter Controls --- */}
            <div className="mb-6 p-4 bg-white rounded-lg shadow grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-1">
                    <label htmlFor="search" className="sr-only">Search by Animal Name</label>
                    <input 
                        type="text" 
                        id="search" 
                        placeholder="Search by animal name..." 
                        value={searchTerm} 
                        onChange={(e) => setSearchTerm(e.target.value)} 
                        className="block w-full form-input rounded-md shadow-sm" 
                    />
                </div>
                <div>
                    <label htmlFor="filter-type" className="sr-only">Filter by Breeding Type</label>
                    <select 
                        id="filter-type" 
                        value={filterType} 
                        onChange={(e) => setFilterType(e.target.value)} 
                        className="block w-full form-select rounded-md shadow-sm"
                    >
                        {typeOptions.map(type => (<option key={type} value={type}>{type === "All" ? "All Breeding Types" : type}</option>))}
                    </select>
                </div>
                <div>
                    <label htmlFor="filter-status" className="sr-only">Filter by Status</label>
                    <select 
                        id="filter-status" 
                        value={filterStatus} 
                        onChange={(e) => setFilterStatus(e.target.value)} 
                        className="block w-full form-select rounded-md shadow-sm"
                    >
                        {statusOptions.map(status => (<option key={status} value={status}>{status === "All" ? "All Statuses" : status}</option>))}
                    </select>
                </div>
            </div>

            {/* --- UPDATED: Charts Section with Grid Layout --- */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800">Breeding Status Overview</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart margin={{ top: 20, right: 60, left: 60, bottom: 20 }}>
                            <Pie data={statusChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} fill="#8884d8" labelLine={false} label={renderCustomizedLabel}>
                                {statusChartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={STATUS_PIE_COLORS[entry.name] || '#cccccc'} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => `${value} event(s)`} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800">Breeding Type Overview</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart margin={{ top: 20, right: 60, left: 60, bottom: 20 }}>
                            <Pie data={eventTypeChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} fill="#8884d8" labelLine={false} label={renderCustomizedLabel}>
                                {eventTypeChartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={EVENT_TYPE_PIE_COLORS[entry.name] || '#cccccc'} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => `${value} event(s)`} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="overflow-x-auto bg-white rounded-lg shadow">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Animal Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Event Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Breeding Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Expected Due Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Status</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredBreedingEvents.length === 0 && (
                            <tr>
                                <td colSpan="6" className="text-center py-10 text-gray-500">No breeding events match the current filters.</td>
                            </tr>
                        )}
                        {filteredBreedingEvents.map(event => {
                            const { daysRemaining, daysCompleted } = calculateDays(event.eventDate, event.dueDate);
                            
                            let displayStatus = event.status;
                            let statusColor = STATUS_PIE_COLORS[event.status] ? `bg-${STATUS_PIE_COLORS[event.status].substring(1)}` : 'bg-yellow-100 text-yellow-800'; // Fallback
                            
                            if (event.status === 'In-Progress' && daysRemaining === 0) {
                                displayStatus = 'Due Today';
                                statusColor = 'bg-blue-100 text-blue-800';
                            } else if (event.status === 'Confirmed') {
                                statusColor = 'bg-green-100 text-green-800';
                            } else if (event.status === 'Failed') {
                                statusColor = 'bg-red-100 text-red-800';
                            } else if (event.status === 'Completed (Calved)') {
                                statusColor = 'bg-purple-100 text-purple-800';
                            } else if (event.status === 'Due') {
                                statusColor = 'bg-blue-100 text-blue-800';
                            }

                            const tooltipContent = (
                                <div className="text-left">
                                    <p>Days Completed: {daysCompleted}</p>
                                    <p>Days Remaining: {daysRemaining}</p>
                                </div>
                            );

                            return (
                                <tr key={event.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600">{event.animalName}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(event.eventDate + 'T00:00:00').toLocaleDateString()}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{event.eventType}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-800 flex items-center">
                                        {new Date(event.dueDate + 'T00:00:00').toLocaleDateString()}
                                        <span className="ml-2">
                                            <InfoIcon tooltip={tooltipContent} />
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColor}`}>
                                            {displayStatus}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button onClick={() => onUpdateStatusClick(event)} className="text-indigo-600 hover:text-indigo-900">
                                            Update Status
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default BreedingDashboard;
